import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import automationBuilderService from '../../../services/automationBuilderService';

function safeJsonParse(raw) {
  if (!raw) return null;
  try { return JSON.parse(String(raw)); } catch { return null; }
}

function sanitizeNode(n) {
  if (!n || typeof n !== 'object') return null;
  const id = n.id != null ? String(n.id) : null;
  const type = n.type != null ? String(n.type) : null;
  if (!id || !type) return null;
  const pos = n.position && typeof n.position === 'object' ? n.position : {};
  const data = n.data && typeof n.data === 'object' ? n.data : {};
  const cfg = data.config && typeof data.config === 'object' ? data.config : {};
  return {
    id,
    type,
    position: { x: Number(pos.x || 0), y: Number(pos.y || 0) },
    data: { label: String(data.label || ''), config: cfg }
  };
}

function sanitizeEdge(e) {
  if (!e || typeof e !== 'object') return null;
  const id = e.id != null ? String(e.id) : null;
  const source = e.source != null ? String(e.source) : null;
  const target = e.target != null ? String(e.target) : null;
  if (!source || !target) return null;
  return {
    id: id || `e_${source}_${target}`,
    source,
    target,
    sourceHandle: e.sourceHandle != null ? String(e.sourceHandle) : null,
    targetHandle: e.targetHandle != null ? String(e.targetHandle) : null
  };
}

function sanitizeGraph({ nodes, edges, viewport }) {
  const vp = viewport && typeof viewport === 'object' ? viewport : {};
  return {
    nodes: (Array.isArray(nodes) ? nodes : []).map(sanitizeNode).filter(Boolean),
    edges: (Array.isArray(edges) ? edges : []).map(sanitizeEdge).filter(Boolean),
    viewport: { x: Number(vp.x || 0), y: Number(vp.y || 0), zoom: typeof vp.zoom === 'number' ? vp.zoom : 1 }
  };
}

function stableGraphKey(graph) {
  try { return JSON.stringify(graph); } catch { return String(Date.now()); }
}

function buildBlockCatalog() {
  return [
    { type: 'trigger.message_received', label: 'Mensagem recebida', group: 'Gatilhos' },
    { type: 'action.send_message', label: 'Enviar mensagem', group: 'Ações' },
    { type: 'action.send_whatsapp', label: 'WhatsApp', group: 'Ações' },
    { type: 'action.send_sms', label: 'SMS', group: 'Ações' },
    { type: 'action.condition', label: 'Condição', group: 'Controle' },
    { type: 'action.delay', label: 'Delay', group: 'Controle' },
    { type: 'action.ia', label: 'IA', group: 'Integrações' },
    { type: 'action.webhook', label: 'Webhook', group: 'Integrações' },
    { type: 'action.http_request', label: 'HTTP request', group: 'Integrações' },
    { type: 'action.api_lookup', label: 'Consulta API', group: 'Integrações' },
    { type: 'action.queue', label: 'Fila atendimento', group: 'Tickets' },
    { type: 'action.transfer_human', label: 'Transferência humano', group: 'Tickets' }
  ];
}

function makeNode({ type, position }) {
  const id = `n_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const catalog = buildBlockCatalog().find((b) => b.type === type);
  const label = catalog ? catalog.label : type;
  const baseConfig = (() => {
    if (type === 'action.delay') return { delayMs: 3000 };
    if (type === 'action.condition') return { expression: '$json.data.message && $json.data.message.body && $json.data.message.body.length > 0' };
    if (type === 'action.send_message' || type === 'action.send_whatsapp' || type === 'action.send_sms') return { messageTemplate: 'Olá!' };
    if (type === 'action.http_request' || type === 'action.webhook' || type === 'action.api_lookup' || type === 'action.ia') return { url: '', method: 'POST', headers: {}, bodyJson: {} };
    if (type === 'action.queue') return { mode: 'set_queue', queueId: null, status: 'queued' };
    if (type === 'action.transfer_human') return { mode: 'assign_user', userId: null, status: 'assigned' };
    return {};
  })();
  return {
    id,
    type,
    position,
    data: {
      label,
      config: baseConfig
    }
  };
}

function NodeCard({ id, data, selected }) {
  const hasError = Boolean(data?.hasError);
  return (
    <Box
      sx={{
        minWidth: 220,
        borderRadius: 2,
        border: '1px solid',
        borderColor: hasError ? 'error.main' : selected ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        boxShadow: selected ? 4 : 1,
        px: 1.5,
        py: 1.2
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{data?.label || id}</Typography>
        {hasError && <Chip size="small" color="error" label="Erro" />}
      </Stack>
      {data?.subtitle && (
        <Typography variant="caption" color="text.secondary">{data.subtitle}</Typography>
      )}
    </Box>
  );
}

const nodeTypes = {
  'trigger.message_received': NodeCard,
  'action.send_message': NodeCard,
  'action.send_whatsapp': NodeCard,
  'action.send_sms': NodeCard,
  'action.condition': NodeCard,
  'action.delay': NodeCard,
  'action.ia': NodeCard,
  'action.webhook': NodeCard,
  'action.http_request': NodeCard,
  'action.api_lookup': NodeCard,
  'action.queue': NodeCard,
  'action.transfer_human': NodeCard
};

function groupBy(items, keyFn) {
  const map = new Map();
  for (const it of items) {
    const k = keyFn(it);
    const arr = map.get(k) || [];
    arr.push(it);
    map.set(k, arr);
  }
  return Array.from(map.entries());
}

function applyValidation(nodes, validation) {
  const ids = new Set((validation?.errors || []).map((e) => String(e.nodeId || '')).filter(Boolean));
  let changed = false;
  const next = nodes.map((n) => {
    const nextHasError = ids.has(String(n.id));
    const prevHasError = Boolean(n?.data?.hasError);
    if (nextHasError === prevHasError) return n;
    changed = true;
    return {
      ...n,
      data: {
        ...(n.data || {}),
        hasError: nextHasError
      }
    };
  });
  return changed ? next : nodes;
}

function AutomationBuilderInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const workflowId = Number(id);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [workflow, setWorkflow] = useState(null);
  const [versions, setVersions] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [propOpen, setPropOpen] = useState(false);
  const [validation, setValidation] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testPayloadRaw, setTestPayloadRaw] = useState('{\n  "data": {\n    "event": "message.received",\n    "message": { "body": "teste" },\n    "contact": { "normalizedNumber": "5511999999999" },\n    "ticket": { "id": 1 }\n  }\n}');
  const [rollbackVersion, setRollbackVersion] = useState('');
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const rf = useReactFlow();
  const rfRef = useRef(null);
  const historyRef = useRef({ past: [], future: [] });
  const savingRef = useRef({ timer: null, validatingTimer: null, suppressHistory: true });
  const lastSavedKeyRef = useRef('');
  const lastValidatedKeyRef = useRef('');
  const validateAbortRef = useRef(null);
  const loadAbortRef = useRef(null);
  const pendingViewportRef = useRef(null);
  const didInitialLoadRef = useRef(false);

  useEffect(() => {
    rfRef.current = rf;
  }, [rf]);

  const selectedNode = useMemo(() => nodes.find((n) => String(n.id) === String(selectedNodeId)) || null, [nodes, selectedNodeId]);
  const catalogGroups = useMemo(() => groupBy(buildBlockCatalog(), (b) => b.group), []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      if (loadAbortRef.current) {
        try { loadAbortRef.current.abort(); } catch (_) {}
      }
      const ac = new AbortController();
      loadAbortRef.current = ac;
      const resp = await automationBuilderService.getWorkflow(workflowId, { signal: ac.signal });
      const data = resp?.data?.data || resp?.data || resp;
      const wf = data?.workflow || null;
      const graph = data?.builder || { nodes: [], edges: [], viewport: { x: 0, y: 0, zoom: 1 } };
      const normalizedGraph = sanitizeGraph(graph || {});
      setWorkflow(wf);
      setVersions(Array.isArray(data?.versions) ? data.versions : []);
      savingRef.current.suppressHistory = true;
      setNodes(normalizedGraph.nodes);
      setEdges(normalizedGraph.edges);
      pendingViewportRef.current = normalizedGraph.viewport || null;
      setTimeout(() => {
        try {
          const vp = pendingViewportRef.current;
          if (vp && typeof vp.zoom === 'number') {
            const api = rfRef.current;
            if (api && typeof api.setViewport === 'function') {
              api.setViewport({ x: vp.x || 0, y: vp.y || 0, zoom: vp.zoom || 1 });
            }
          }
        } catch (_) {}
        savingRef.current.suppressHistory = false;
      }, 50);
      didInitialLoadRef.current = true;
    } catch (e) {
      const msg = e?.message || 'Falha ao carregar workflow';
      setLoadError(msg);
      toast.error(msg);
      if (e?.status === 401) {
        navigate('/login');
      }
      if (e?.status === 404) {
        navigate('/automation');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, setEdges, setNodes, workflowId]);

  useEffect(() => {
    if (!workflowId) return;
    if (didInitialLoadRef.current) return;
    load();
  }, [workflowId, load]);

  const pushHistory = useCallback((nextNodes, nextEdges) => {
    if (savingRef.current.suppressHistory) return;
    const vp = rf.getViewport();
    const snap = {
      nodes: nextNodes,
      edges: nextEdges,
      viewport: { x: vp.x, y: vp.y, zoom: vp.zoom }
    };
    historyRef.current.past.push(snap);
    if (historyRef.current.past.length > 60) historyRef.current.past.shift();
    historyRef.current.future = [];
  }, [rf]);

  useEffect(() => {
    pushHistory(nodes, edges);
  }, [nodes, edges, pushHistory]);

  useEffect(() => {
    const handler = (e) => {
      const key = String(e.key || '').toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      if (key === 'z') {
        e.preventDefault();
        const past = historyRef.current.past;
        if (past.length < 2) return;
        const curr = past.pop();
        historyRef.current.future.unshift(curr);
        const prev = past[past.length - 1];
        savingRef.current.suppressHistory = true;
        setNodes(prev.nodes);
        setEdges(prev.edges);
        setTimeout(() => {
          try { rf.setViewport(prev.viewport); } catch (_) {}
          savingRef.current.suppressHistory = false;
        }, 0);
      }
      if (key === 'y') {
        e.preventDefault();
        const future = historyRef.current.future;
        if (!future.length) return;
        const next = future.shift();
        historyRef.current.past.push(next);
        savingRef.current.suppressHistory = true;
        setNodes(next.nodes);
        setEdges(next.edges);
        setTimeout(() => {
          try { rf.setViewport(next.viewport); } catch (_) {}
          savingRef.current.suppressHistory = false;
        }, 0);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [rf, setNodes, setEdges]);

  const scheduleSave = useCallback(() => {
    if (!workflowId) return;
    if (savingRef.current.timer) clearTimeout(savingRef.current.timer);
    savingRef.current.timer = setTimeout(async () => {
      try {
        const vp = rf.getViewport();
        const graph = sanitizeGraph({ nodes, edges, viewport: { x: vp.x, y: vp.y, zoom: vp.zoom } });
        const key = stableGraphKey(graph);
        if (key === lastSavedKeyRef.current) return;
        lastSavedKeyRef.current = key;
        await automationBuilderService.saveDraft(workflowId, { name: workflow?.name, graph });
      } catch (_) {}
    }, 800);
  }, [workflowId, nodes, edges, rf, workflow?.name]);

  const scheduleValidate = useCallback(() => {
    if (!workflowId) return;
    if (savingRef.current.validatingTimer) clearTimeout(savingRef.current.validatingTimer);
    savingRef.current.validatingTimer = setTimeout(async () => {
      try {
        if (validateAbortRef.current) {
          try { validateAbortRef.current.abort(); } catch (_) {}
        }
        const ac = new AbortController();
        validateAbortRef.current = ac;
        const vp = rf.getViewport();
        const graph = sanitizeGraph({ nodes, edges, viewport: { x: vp.x, y: vp.y, zoom: vp.zoom } });
        const key = stableGraphKey(graph);
        if (key === lastValidatedKeyRef.current) return;
        lastValidatedKeyRef.current = key;
        const resp = await automationBuilderService.validate(workflowId, { graph }, { signal: ac.signal });
        const data = resp?.data?.data || resp?.data || resp;
        setValidation(data);
        setNodes((curr) => applyValidation(curr, data));
      } catch (_) {}
    }, 400);
  }, [workflowId, nodes, edges, rf, setNodes]);

  useEffect(() => {
    if (loading) return;
    if (!didInitialLoadRef.current) return;
    if (!workflow) return;
    scheduleSave();
    scheduleValidate();
  }, [nodes, edges, loading, scheduleSave, scheduleValidate, workflow]);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge({ ...params, animated: true }, eds)), [setEdges]);

  const onDragStart = (e, type) => {
    e.dataTransfer.setData('application/reactflow', type);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/reactflow');
    if (!type) return;
    const bounds = e.currentTarget.getBoundingClientRect();
    const position = rf.project({
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top
    });
    const node = makeNode({ type, position });
    setNodes((nds) => nds.concat(node));
  }, [rf, setNodes]);

  const updateNodeConfig = (nodeId, patch) => {
    setNodes((curr) => curr.map((n) => {
      if (String(n.id) !== String(nodeId)) return n;
      return {
        ...n,
        data: {
          ...(n.data || {}),
          config: { ...((n.data || {}).config || {}), ...(patch || {}) }
        }
      };
    }));
  };

  const publish = async () => {
    try {
      const resp = await automationBuilderService.publish(workflowId);
      const data = resp?.data?.data || resp?.data || resp;
      toast.success(`Publicado (versão ${data?.version || ''})`);
      await load();
    } catch (e) {
      const validationResp = e?.payload?.validation || e?.validation || null;
      if (validationResp) {
        setValidation(validationResp);
        setNodes((curr) => applyValidation(curr, validationResp));
        toast.error('Fluxo inválido. Corrija os erros.');
      } else {
        toast.error(e?.message || 'Falha ao publicar');
      }
    }
  };

  const runTest = async () => {
    const parsed = safeJsonParse(testPayloadRaw);
    if (!parsed) {
      toast.error('JSON inválido');
      return;
    }
    try {
      const resp = await automationBuilderService.test(workflowId, { payload: parsed });
      const data = resp?.data?.data || resp?.data || resp;
      toast.success('Execução enviada');
      setTestPayloadRaw(JSON.stringify(parsed, null, 2));
      setTestOpen(false);
      if (data?.result) {
        toast.info(typeof data.result === 'string' ? data.result : 'Executado');
      }
    } catch (e) {
      toast.error(e?.message || 'Falha ao executar teste');
    }
  };

  const doRollback = async () => {
    const v = Number(rollbackVersion);
    if (!v) return;
    try {
      await automationBuilderService.rollback(workflowId, { version: v });
      toast.success(`Rollback aplicado (para v${v})`);
      setRollbackOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha no rollback');
    }
  };

  const doDelete = async () => {
    try {
      await automationBuilderService.deleteWorkflow(workflowId);
      toast.success('Fluxo excluído');
      navigate('/automation');
    } catch (e) {
      toast.error(e?.message || 'Falha ao excluir fluxo');
    }
  };

  const canUndo = historyRef.current.past.length > 1;
  const canRedo = historyRef.current.future.length > 0;

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }
  if (loadError) {
    return (
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography color="error">{loadError}</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => { didInitialLoadRef.current = false; load(); }}>
              Tentar novamente
            </Button>
            <Button variant="contained" onClick={() => navigate('/automation')}>
              Voltar
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex' }}>
      <Drawer variant="persistent" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 280 } }}>
        <Box sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Blocos</Typography>
            {catalogGroups.map(([group, items]) => (
              <Box key={group}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {group}
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {items.map((b) => (
                    <Button
                      key={b.type}
                      variant="outlined"
                      size="small"
                      draggable
                      onDragStart={(e) => onDragStart(e, b.type)}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      {b.label}
                    </Button>
                  ))}
                </Stack>
                <Divider sx={{ my: 2 }} />
              </Box>
            ))}
          </Stack>
        </Box>
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="outlined" onClick={() => navigate('/automation')}>Voltar</Button>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{workflow?.name || `Workflow ${workflowId}`}</Typography>
              {validation?.ok === false && <Chip size="small" color="error" label="Inválido" />}
              {validation?.ok === true && <Chip size="small" color="success" label="OK" />}
              {workflow?.engineWorkflowId && <Chip size="small" label={`n8n: ${workflow.engineWorkflowId}`} />}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Button size="small" variant="outlined" onClick={() => setDrawerOpen((v) => !v)}>
                {drawerOpen ? 'Ocultar blocos' : 'Mostrar blocos'}
              </Button>
              <Button size="small" variant="outlined" disabled={!canUndo} onClick={() => {
                const past = historyRef.current.past;
                if (past.length < 2) return;
                const curr = past.pop();
                historyRef.current.future.unshift(curr);
                const prev = past[past.length - 1];
                savingRef.current.suppressHistory = true;
                setNodes(prev.nodes);
                setEdges(prev.edges);
                setTimeout(() => {
                  try { rf.setViewport(prev.viewport); } catch (_) {}
                  savingRef.current.suppressHistory = false;
                }, 0);
              }}>
                Undo
              </Button>
              <Button size="small" variant="outlined" disabled={!canRedo} onClick={() => {
                const future = historyRef.current.future;
                if (!future.length) return;
                const next = future.shift();
                historyRef.current.past.push(next);
                savingRef.current.suppressHistory = true;
                setNodes(next.nodes);
                setEdges(next.edges);
                setTimeout(() => {
                  try { rf.setViewport(next.viewport); } catch (_) {}
                  savingRef.current.suppressHistory = false;
                }, 0);
              }}>
                Redo
              </Button>
              <Button size="small" variant="outlined" onClick={() => setRollbackOpen(true)} disabled={!versions.length}>
                Rollback
              </Button>
              <Button size="small" variant="outlined" onClick={() => setTestOpen(true)}>
                Teste
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
                Excluir
              </Button>
              <Button size="small" variant="contained" onClick={publish}>
                Publicar
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0 }} onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
            onNodeClick={(_, node) => {
              setSelectedNodeId(node.id);
              setPropOpen(true);
            }}
          >
            <MiniMap />
            <Controls />
            <Background />
          </ReactFlow>
        </Box>
      </Box>

      <Drawer anchor="right" variant="persistent" open={propOpen} onClose={() => setPropOpen(false)} PaperProps={{ sx: { width: 360 } }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Propriedades</Typography>
            <Button size="small" onClick={() => setPropOpen(false)}>Fechar</Button>
          </Stack>

          {!selectedNode ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Selecione um bloco para editar.</Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Título"
                value={selectedNode?.data?.label || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setNodes((curr) => curr.map((n) => (String(n.id) === String(selectedNode.id) ? { ...n, data: { ...(n.data || {}), label: v } } : n)));
                }}
              />

              <Box sx={{ mt: 2 }}>
                {String(selectedNode.type) === 'action.send_message' || String(selectedNode.type) === 'action.send_whatsapp' || String(selectedNode.type) === 'action.send_sms' ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Mensagem"
                    value={selectedNode?.data?.config?.messageTemplate || ''}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { messageTemplate: e.target.value })}
                    helperText='Use texto literal ou expressão: = "Olá " + $json.data.contact.number'
                  />
                ) : null}

                {String(selectedNode.type) === 'action.condition' ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Expressão (retorna true/false)"
                    value={selectedNode?.data?.config?.expression || ''}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { expression: e.target.value })}
                    helperText='Ex.: $json.data.message.body.includes("oi")'
                  />
                ) : null}

                {String(selectedNode.type) === 'action.delay' ? (
                  <TextField
                    fullWidth
                    type="number"
                    label="Delay (ms)"
                    value={selectedNode?.data?.config?.delayMs ?? 0}
                    onChange={(e) => updateNodeConfig(selectedNode.id, { delayMs: Number(e.target.value) })}
                  />
                ) : null}

                {['action.http_request', 'action.webhook', 'action.api_lookup', 'action.ia'].includes(String(selectedNode.type)) ? (
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="URL"
                      value={selectedNode?.data?.config?.url || ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { url: e.target.value })}
                    />
                    <FormControl fullWidth>
                      <InputLabel>Método</InputLabel>
                      <Select
                        label="Método"
                        value={selectedNode?.data?.config?.method || 'POST'}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { method: e.target.value })}
                      >
                        <MenuItem value="GET">GET</MenuItem>
                        <MenuItem value="POST">POST</MenuItem>
                        <MenuItem value="PUT">PUT</MenuItem>
                        <MenuItem value="PATCH">PATCH</MenuItem>
                        <MenuItem value="DELETE">DELETE</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Headers (JSON)"
                      value={JSON.stringify(selectedNode?.data?.config?.headers || {}, null, 2)}
                      onChange={(e) => {
                        const parsed = safeJsonParse(e.target.value);
                        if (parsed) updateNodeConfig(selectedNode.id, { headers: parsed });
                      }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      label="Body (JSON)"
                      value={JSON.stringify(selectedNode?.data?.config?.bodyJson || {}, null, 2)}
                      onChange={(e) => {
                        const parsed = safeJsonParse(e.target.value);
                        if (parsed) updateNodeConfig(selectedNode.id, { bodyJson: parsed });
                      }}
                    />
                  </Stack>
                ) : null}

                {String(selectedNode.type) === 'action.queue' ? (
                  <Stack spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Modo</InputLabel>
                      <Select
                        label="Modo"
                        value={selectedNode?.data?.config?.mode || 'set_queue'}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { mode: e.target.value })}
                      >
                        <MenuItem value="set_queue">Mover para fila</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      type="number"
                      label="Queue ID"
                      value={selectedNode?.data?.config?.queueId ?? ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { queueId: e.target.value ? Number(e.target.value) : null })}
                    />
                  </Stack>
                ) : null}

                {String(selectedNode.type) === 'action.transfer_human' ? (
                  <Stack spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Modo</InputLabel>
                      <Select
                        label="Modo"
                        value={selectedNode?.data?.config?.mode || 'assign_user'}
                        onChange={(e) => updateNodeConfig(selectedNode.id, { mode: e.target.value })}
                      >
                        <MenuItem value="assign_user">Atribuir operador</MenuItem>
                        <MenuItem value="unassign">Remover atribuição</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      type="number"
                      label="User ID"
                      value={selectedNode?.data?.config?.userId ?? ''}
                      onChange={(e) => updateNodeConfig(selectedNode.id, { userId: e.target.value ? Number(e.target.value) : null })}
                    />
                  </Stack>
                ) : null}
              </Box>

              <Divider sx={{ my: 2 }} />
              <Button
                color="error"
                variant="outlined"
                onClick={() => {
                  setNodes((curr) => curr.filter((n) => String(n.id) !== String(selectedNode.id)));
                  setEdges((curr) => curr.filter((e) => String(e.source) !== String(selectedNode.id) && String(e.target) !== String(selectedNode.id)));
                  setSelectedNodeId(null);
                  setPropOpen(false);
                }}
              >
                Remover bloco
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      <Dialog open={testOpen} onClose={() => setTestOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Execução de teste</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Envia um payload real para o workflow via webhook do n8n (precisa estar publicado).
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={14}
            value={testPayloadRaw}
            onChange={(e) => setTestPayloadRaw(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={runTest}>Executar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rollbackOpen} onClose={() => setRollbackOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rollback</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Versão</InputLabel>
            <Select label="Versão" value={rollbackVersion} onChange={(e) => setRollbackVersion(e.target.value)}>
              {versions.map((v) => (
                <MenuItem key={v.id} value={String(v.version)}>{`v${v.version}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={doRollback} disabled={!rollbackVersion}>Aplicar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Excluir fluxo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Excluir o fluxo "{workflow?.name || `Workflow ${workflowId}`}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={doDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function AutomationBuilder() {
  return (
    <ReactFlowProvider>
      <AutomationBuilderInner />
    </ReactFlowProvider>
  );
}
